import { useState, useEffect } from "react";
import API from "@/lib/api";
import { Package, Sparkles } from "lucide-react";

/**
 * "What's included in your GLPFlo delivery" — surfaced on the patient
 * dashboard once they have at least one paid order, so the patient knows
 * exactly what's arriving at their door.
 *
 * Modes:
 *   - global view  → no `orderId` prop, fetches `/delivery-includes/global`.
 *   - per-order   → pass `orderId`, fetches the per-order override and falls
 *                    back to the global list when no override is set.
 */
export default function DeliveryIncludesCard({ orderId = null }) {
  const [data, setData] = useState(null);
  const [override, setOverride] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [g, o] = await Promise.all([
          API.get("/delivery-includes/global"),
          orderId ? API.get(`/delivery-includes/order/${orderId}`).catch(() => ({ data: null })) : Promise.resolve({ data: null }),
        ]);
        if (!alive) return;
        setData(g.data);
        setOverride(o.data || null);
      } catch (_e) {
        // Silently fall back to nothing — this card is non-critical UX.
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [orderId]);

  if (loading || !data) return null;
  const items = (override && override.items && override.items.length > 0) ? override.items : data.items;
  const note = (override && override.note) ? override.note : data.footer_note;

  return (
    <div
      className="bg-gradient-to-br from-purple-50 via-white to-purple-50 border border-purple-200 rounded-2xl p-5 sm:p-6 shadow-[0_2px_12px_rgba(124,58,237,0.06)]"
      data-testid={orderId ? `delivery-includes-order-${orderId}` : "delivery-includes-global"}
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-purple-600 flex items-center justify-center shrink-0">
          <Package className="w-5 h-5 text-white" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
            {data.headline}
          </h3>
          {override && (
            <p className="text-[11px] uppercase tracking-wider text-purple-700 font-semibold mt-0.5">
              <Sparkles className="w-3 h-3 inline -mt-0.5 mr-0.5" /> Customized for this order
            </p>
          )}
        </div>
      </div>
      <ul className="space-y-2 mb-4" data-testid="delivery-includes-items">
        {items.map((item, idx) => (
          <li key={idx} className="flex items-start gap-2 text-sm text-slate-700">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-purple-500 shrink-0" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
      {note && (
        <p className="text-xs text-slate-500 leading-relaxed border-t border-purple-100 pt-3">
          {note}
        </p>
      )}
    </div>
  );
}
